import api, { ApiError, signOutOnUnauthorized } from './client';
import { useAuthStore } from '../store/auth';
import type { ApiResponse } from './types';
import { API_FETCH_URL } from './base';

export interface Playable {
  id: string;
  name: string;
  storage_prefix: string;
  index_path: string;
  size_bytes: number;
  status: string;
  created_at: string;
}

export interface PlayableLink {
  id: string;
  domain_id: string;
  domain_hostname?: string;
  slug: string;
  name: string;
  status: string;
  notes: string | null;
  created_at: string;
  items_count?: number;
}

export interface PlayableLinkItem {
  id: string;
  link_id: string;
  playable_id: string;
  target_campaign_id: string;
  weight: number;
  status: string;
  created_at: string;
  playable_name?: string;
  campaign_name?: string;
  campaign_slug?: string;
}

export interface PlayableLinkDetail extends PlayableLink {
  items: PlayableLinkItem[];
}

export const getPlayables = () => api.get<Playable[]>('/admin/tracker/playables');
export const getPlayable = (id: string) => api.get<Playable>(`/admin/tracker/playables/${id}`);
export const updatePlayable = (id: string, body: Partial<Pick<Playable, 'name' | 'status' | 'index_path'>>) =>
  api.patch<Playable>(`/admin/tracker/playables/${id}`, body);
export const deletePlayable = (id: string) => api.delete<{ deleted: boolean }>(`/admin/tracker/playables/${id}`);

// Multipart upload (zip + name + optional index_path).
const BASE_URL = API_FETCH_URL;
export async function uploadPlayable(file: File, name: string, indexPath?: string): Promise<Playable> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('name', name);
  if (indexPath) fd.append('index_path', indexPath);

  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE_URL}/admin/tracker/playables`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (res.status === 401) {
    await signOutOnUnauthorized('POST', `${BASE_URL}/admin/tracker/playables`, res);
  }
  const data: ApiResponse<Playable> = await res.json();
  if (!res.ok || !data.success) throw new ApiError(data.error || `Upload failed (${res.status})`, res.status, data);
  return data.data as Playable;
}

export const getPlayableLinks = () => api.get<PlayableLink[]>('/admin/tracker/playable-links');
export const getPlayableLink = (id: string) => api.get<PlayableLinkDetail>(`/admin/tracker/playable-links/${id}`);
export const createPlayableLink = (body: { domain_id: string; slug: string; name: string; notes?: string }) =>
  api.post<PlayableLink>('/admin/tracker/playable-links', body);
export const updatePlayableLink = (id: string, body: Partial<Pick<PlayableLink, 'name' | 'status' | 'notes' | 'slug' | 'domain_id'>>) =>
  api.patch<PlayableLink>(`/admin/tracker/playable-links/${id}`, body);
export const deletePlayableLink = (id: string) => api.delete<{ deleted: boolean }>(`/admin/tracker/playable-links/${id}`);

export const addPlayableLinkItem = (linkId: string, body: { playable_id: string; target_campaign_id: string; weight?: number }) =>
  api.post<PlayableLinkItem>(`/admin/tracker/playable-links/${linkId}/items`, body);
export const updatePlayableLinkItem = (linkId: string, itemId: string, body: Partial<Pick<PlayableLinkItem, 'weight' | 'status' | 'playable_id' | 'target_campaign_id'>>) =>
  api.patch<PlayableLinkItem>(`/admin/tracker/playable-links/${linkId}/items/${itemId}`, body);
export const deletePlayableLinkItem = (linkId: string, itemId: string) =>
  api.delete<{ deleted: boolean }>(`/admin/tracker/playable-links/${linkId}/items/${itemId}`);

export interface PlayableLinkStatsRow {
  id: string;
  playable_id: string;
  target_campaign_id: string;
  weight: number;
  status: string;
  playable_name: string | null;
  campaign_name: string | null;
  campaign_slug: string | null;
  hits: number;
  unique_hits: number;
  flow_clicks: number;
  regs: number;
  deps: number;
  payout_cents: number;
}

export interface PlayableLinkStats {
  rows: PlayableLinkStatsRow[];
  totals: {
    hits: number;
    unique_hits: number;
    flow_clicks: number;
    regs: number;
    deps: number;
    payout_cents: number;
  };
}

export function getPlayableLinkStats(linkId: string, range?: { from?: string; to?: string }): Promise<PlayableLinkStats> {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to)   params.set('to', range.to);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return api.get<PlayableLinkStats>(`/admin/tracker/playable-links/${linkId}/stats${qs}`);
}
